import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Zap, Hand } from 'lucide-react';

interface ScheduleRun {
    id: string;
    scheduleName: string;
    triggerType: 'AUTOMATIC' | 'MANUAL';
    status: string;
    startTime: string;
    endTime: string | null;
    processedCount: number;
    totalCount: number;
    sentCount: number;
    failedCount: number;
    skippedCount: number;
    errorDetails?: string | null;
}

interface Props {
    token: string;
}

const getApiBaseUrl = () => {
    let url = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    return url.endsWith('/') ? url.slice(0, -1) : url;
};

const API_BASE_URL = getApiBaseUrl();

const STATUS_BADGES: Record<string, string> = {
    COMPLETED: 'bg-green-100 text-green-700',
    RUNNING: 'bg-blue-100 text-blue-700',
    FAILED: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-gray-100 text-gray-600'
};

export default function ScheduleRunLogsTab({ token }: Props) {
    const [page, setPage] = useState(1);
    const limit = 20;
    const [data, setData] = useState<{ data: ScheduleRun[]; pagination: any } | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchRuns = useCallback(async (silent = false) => {
        if (!silent) setLoading(true); else setRefreshing(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/saas/schedules/logs?page=${page}&limit=${limit}`, {
                headers: { Authorization: `Bearer ${token}`, 'x-client-type': 'web' }
            });
            const json = await res.json();
            if (json.success) setData(json);
        } catch { /* ignore */ }
        finally { setLoading(false); setRefreshing(false); }
    }, [token, page]);

    useEffect(() => { fetchRuns(); }, [fetchRuns]);

    const runs = data?.data ?? [];
    const total = data?.pagination?.total || 0;
    const totalPages = data?.pagination?.totalPages || 0;

    const formatDateTime = (d: string) =>
        new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(d));

    const duration = (run: ScheduleRun) => {
        if (!run.endTime) return '—';
        const ms = new Date(run.endTime).getTime() - new Date(run.startTime).getTime();
        if (ms < 1000) return '<1s';
        const s = Math.round(ms / 1000);
        return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
    };

    if (loading) return (
        <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2">
                <button
                    onClick={() => fetchRuns(true)}
                    disabled={refreshing}
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                    title="Refresh"
                >
                    <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin text-indigo-600' : ''}`} />
                </button>
                <span className="text-sm font-medium text-gray-500">Schedule Run Logs ({total})</span>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Schedule', 'Trigger', 'Status', 'Started', 'Duration', 'Sent', 'Failed', 'Skipped', 'Details'].map(h => (
                                    <th key={h} className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {runs.map(run => (
                                <tr key={run.id} className="hover:bg-indigo-50/30 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm font-medium text-gray-900 uppercase tracking-wide">{run.scheduleName.replace(/_/g, ' ')}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {run.triggerType === 'AUTOMATIC' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
                                                <Zap className="h-3 w-3" />
                                                Automatic
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-700">
                                                <Hand className="h-3 w-3" />
                                                Manual
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_BADGES[run.status] || 'bg-gray-100 text-gray-600'}`}>
                                            {run.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">{formatDateTime(run.startTime)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">{duration(run)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-700">{run.sentCount}</td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${run.failedCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{run.failedCount}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{run.skippedCount}</td>
                                    <td className="px-6 py-4 text-xs text-gray-500 max-w-xs">
                                        {run.status === 'RUNNING'
                                            ? `${run.processedCount}${run.totalCount ? ` / ${run.totalCount}` : ''} processed`
                                            : (run.errorDetails
                                                ? <span className="text-red-600 truncate block" title={run.errorDetails}>{run.errorDetails}</span>
                                                : `${run.processedCount} processed`)}
                                    </td>
                                </tr>
                            ))}
                            {runs.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-6 py-12 text-center text-gray-400 text-sm">No schedule runs recorded yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-700">
                        Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex gap-1">
                        <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Prev</button>
                        <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages} className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}
        </div>
    );
}
